import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Breadcrumb } from '@/components/ui/breadcrumb';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { LoadingOverlay } from '@/components/ui/loading-overlay';
import { useAuthenticatedFetch } from '@/hooks/useAuthenticatedFetch';
import { formatTimestamp, formatRelativeTime } from '@/lib/formatters';
import type { AdminUserChatsResponse, AdminChatRecord } from '@/types/admin';

export default function UserChatsPage() {
  const { userId } = useParams<{ userId: string }>();
  const authenticatedFetch = useAuthenticatedFetch();
  const [tab, setTab] = useState<'ai' | 'character'>('ai');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // AI chats state
  const [allAiChats, setAllAiChats] = useState<AdminChatRecord[]>([]);
  const [aiLastKey, setAiLastKey] = useState<Record<string, unknown> | undefined>();
  const [aiHasMore, setAiHasMore] = useState(false);
  const [aiLoadingMore, setAiLoadingMore] = useState(false);
  // Character chats state
  const [allCharChats, setAllCharChats] = useState<AdminChatRecord[]>([]);
  const [charLastKey, setCharLastKey] = useState<Record<string, unknown> | undefined>();
  const [charHasMore, setCharHasMore] = useState(false);
  const [charLoadingMore, setCharLoadingMore] = useState(false);

  const fetchBoth = async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await authenticatedFetch(`users/${userId}/chats?limit=25`);
      if (!res.ok) throw new Error(`Failed to load chats (${res.status})`);
      const payload = (await res.json()) as AdminUserChatsResponse;
      setAllAiChats(payload.aiChats);
      setAiLastKey(payload.aiChatsLastKey);
      setAiHasMore(payload.aiChatsHasMore);
      setAllCharChats(payload.characterChats);
      setCharLastKey(payload.characterChatsLastKey);
      setCharHasMore(payload.characterChatsHasMore);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load chats');
    } finally {
      setLoading(false);
    }
  };

  const loadMoreAi = async () => {
    if (!userId || !aiLastKey) return;
    setAiLoadingMore(true);
    try {
      const params = new URLSearchParams({ limit: '25', aiLastKey: JSON.stringify(aiLastKey) });
      const res = await authenticatedFetch(`users/${userId}/chats?${params}`);
      if (!res.ok) throw new Error(`Failed to load more (${res.status})`);
      const payload = (await res.json()) as AdminUserChatsResponse;
      setAllAiChats((prev) => [...prev, ...payload.aiChats]);
      setAiLastKey(payload.aiChatsLastKey);
      setAiHasMore(payload.aiChatsHasMore);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load more');
    } finally {
      setAiLoadingMore(false);
    }
  };

  const loadMoreChar = async () => {
    if (!userId || !charLastKey) return;
    setCharLoadingMore(true);
    try {
      const params = new URLSearchParams({ limit: '25', characterLastKey: JSON.stringify(charLastKey) });
      const res = await authenticatedFetch(`users/${userId}/chats?${params}`);
      if (!res.ok) throw new Error(`Failed to load more (${res.status})`);
      const payload = (await res.json()) as AdminUserChatsResponse;
      setAllCharChats((prev) => [...prev, ...payload.characterChats]);
      setCharLastKey(payload.characterChatsLastKey);
      setCharHasMore(payload.characterChatsHasMore);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load more');
    } finally {
      setCharLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchBoth();
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <LoadingOverlay visible={loading} message="Loading chats..." />
      <div className="space-y-4 sm:space-y-6">
        <Breadcrumb
          items={[
            { label: 'Dashboard', to: '/' },
            { label: 'Users', to: '/users' },
            { label: userId ?? 'User', to: `/users/${userId}` },
            { label: 'Chats' },
          ]}
        />
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Chat data</h1>
            <p className="text-muted-foreground font-mono text-xs mt-1">{userId}</p>
          </div>
          <Button variant="outline" size="sm" asChild>
            <Link to={`/users/${userId}/cost-audit`}>Cost Audit →</Link>
          </Button>
        </div>

        {error && (
          <div className="rounded-md border border-destructive/50 bg-destructive/5 p-3 text-sm text-destructive">
            {error}
          </div>
        )}

        <Tabs value={tab} onValueChange={(v) => setTab(v as 'ai' | 'character')}>
          <TabsList>
            <TabsTrigger value="ai">
              AI Chats
              {allAiChats.length > 0 && (
                <Badge variant="secondary" className="ml-2">{allAiChats.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="character">
              Character Chats
              {allCharChats.length > 0 && (
                <Badge variant="secondary" className="ml-2">{allCharChats.length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="ai" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>AI Chats (aichats-table)</CardTitle>
                <CardDescription>
                  {allAiChats.length} chats loaded{aiHasMore ? ' — more available' : ''}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {allAiChats.length > 0 ? (
                  <>
                    <div className="overflow-hidden rounded-md border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Chat ID</TableHead>
                            <TableHead>Title</TableHead>
                            <TableHead>Toolbox</TableHead>
                            <TableHead>Project</TableHead>
                            <TableHead>Updated</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {allAiChats.map((chat) => (
                            <TableRow key={chat.id}>
                              <TableCell className="font-mono text-xs">{chat.id}</TableCell>
                              <TableCell className="max-w-xs truncate">
                                {chat.title ?? <span className="text-muted-foreground">—</span>}
                                {chat.summary && (
                                  <p className="text-xs text-muted-foreground truncate">{chat.summary}</p>
                                )}
                              </TableCell>
                              <TableCell>
                                {chat.toolbox ? <Badge variant="outline">{chat.toolbox}</Badge> : '—'}
                              </TableCell>
                              <TableCell className="font-mono text-xs">{chat.projectId ?? '—'}</TableCell>
                              <TableCell className="text-xs">
                                {chat.updated ? (
                                  <span title={formatTimestamp(chat.updated)}>
                                    {formatRelativeTime(chat.updated)}
                                  </span>
                                ) : '—'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    {aiHasMore && (
                      <div className="mt-3">
                        <Button variant="outline" size="sm" onClick={loadMoreAi} disabled={aiLoadingMore}>
                          {aiLoadingMore ? 'Loading…' : 'Load more'}
                        </Button>
                      </div>
                    )}
                  </>
                ) : (
                  !loading && <p className="text-sm text-muted-foreground">No AI chats found.</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="character" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Character Chats (chats-table)</CardTitle>
                <CardDescription>
                  {allCharChats.length} chats loaded{charHasMore ? ' — more available' : ''}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {allCharChats.length > 0 ? (
                  <>
                    <div className="overflow-hidden rounded-md border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Chat ID</TableHead>
                            <TableHead>Character</TableHead>
                            <TableHead>Project</TableHead>
                            <TableHead>Created</TableHead>
                            <TableHead>Updated</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {allCharChats.map((chat) => (
                            <TableRow key={chat.id}>
                              <TableCell className="font-mono text-xs">{chat.id}</TableCell>
                              <TableCell>{chat.characterName ?? <span className="text-muted-foreground">—</span>}</TableCell>
                              <TableCell className="font-mono text-xs">{chat.projectId ?? '—'}</TableCell>
                              <TableCell className="text-xs">{formatTimestamp(chat.created)}</TableCell>
                              <TableCell className="text-xs">
                                {chat.updated ? (
                                  <span title={formatTimestamp(chat.updated)}>
                                    {formatRelativeTime(chat.updated)}
                                  </span>
                                ) : '—'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    {charHasMore && (
                      <div className="mt-3">
                        <Button variant="outline" size="sm" onClick={loadMoreChar} disabled={charLoadingMore}>
                          {charLoadingMore ? 'Loading…' : 'Load more'}
                        </Button>
                      </div>
                    )}
                  </>
                ) : (
                  !loading && <p className="text-sm text-muted-foreground">No character chats found.</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}
